
import { throwError, Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '../../../../environments/environment';
import { SpinnerService } from '../../../app-module/spinner/spinner.service';
import { ActivatedRoute } from '@angular/router';
import { MessagesService } from '../services/messages.service';
import { TranslationService } from '../services/translation.service';
import { htmlToPlainText } from '../functions/js-linq';
import { finalize, catchError } from 'rxjs/operators';
import { TokenResolverService } from '../resolvers/token-resolver.service';

@Injectable()
export class AuthHttpInterceptor implements HttpInterceptor {
    private numberOfActiveRequests = 0;
    constructor(public spinnerService: SpinnerService,
        public messagesService: MessagesService,
        public route: ActivatedRoute,
        public dialog: MatDialog,
        private translationService: TranslationService,
        private tokenResolverService: TokenResolverService) { }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        this.spinnerService.toggleSpinner(true);
        this.numberOfActiveRequests++;

        let ignoreExceptionHandling = req.headers && req.headers.get('ignoreExceptionHandling') === 'true'

        const headers = {
            'Authorization': `Bearer ${this.getToken()}`,
            'Pragma': 'no-cache',
            'Cache-Control': 'no-cache',
            'Expires': 'Sat, 01 Dec 2001 00:00:00 GMT'
        };

        if (req.url.indexOf('file') === -1) {
            headers['Content-Type'] = 'application/json';
        }

        if(req.params.get('deleteContentType') && req.params.get('deleteContentType') === 'true') {
            delete headers['Content-Type'];
        }

        // This is failsafe in case that req.url starts with / as it would make url invalid in production
        const preparedRequestUrl : string = req.url[0] == '/' ? req.url.substring(1) : req.url;
        var cloneRequestArgs = {
            setHeaders: headers,
            url: `${environment.url}/${preparedRequestUrl}`
        }

        if(req.headers.has('ignoreexceptionhandling')){
            cloneRequestArgs['headers'] =  req.headers.delete('ignoreexceptionhandling');
        }

        const authReq = req.clone(cloneRequestArgs);
        return next.handle(authReq)
            .pipe(
                finalize(() => {
                    this.numberOfActiveRequests--;
                    if(this.numberOfActiveRequests === 0){
                        this.spinnerService.toggleSpinner(false);
                    }
                }),
                catchError(async (error: HttpErrorResponse) => {
                    if(ignoreExceptionHandling){
                        throw error;
                    }

                    let parsedError;
                    if (error.status === 503) {
                        window.location.href = `${window.location.origin}/TransientError.html`;
                    } else if(error.error && error.error.error){
                        parsedError = error.error.error;

                        if (typeof error.error.error === 'string') {
                            parsedError = JSON.parse(error.error.error);
                        }

                        if(error.status === 403) {
                          this.messagesService.showConfirmDialog({
                            title: this.translationService.translate('Authentication.FailedMsg'),
                            content: this.translationService.translate('Authentication.FailedMsgForReload')
                          }).afterClosed().subscribe((isConfirmed: boolean) => {
                            if(isConfirmed) {
                              window.location.href = `${window.location.origin}/Logoff.aspx`;
                            }
                          });
                        } else {
                          this.messagesService.showAlertDialog({
                            title: this.translationService.translate('Msg.Error'),
                            content: this.formatErrorMessage(parsedError)
                          });
                        }
                    } else if (error.error && !(error.error instanceof Blob)) {
                        parsedError = error.error;

                        if (typeof error.error === 'string') {
                            parsedError = JSON.parse(error.error);
                        }
                        this.messagesService.showAlertDialog({
                            title: this.translationService.translate('Msg.Error'),
                            content: this.formatErrorMessage(parsedError)
                        });
                    } else if (error.error && error.error instanceof Blob) {
                        parsedError = JSON.parse(await error.error.text());
                        parsedError = parsedError.error;
                        this.messagesService.showAlertDialog({
                            title: this.translationService.translate('Msg.Error'),
                            content: this.formatErrorMessage(parsedError)
                        });
                    } else {
                        if (error.status === 403) {
                            this.messagesService.showAlertDialog({
                                title: this.translationService.translate('Msg.Error'),
                                content: this.translationService.translate('Admin.NoPermission')
                            });
                        } else {
                            this.messagesService.showAlertDialog({
                                title: error.statusText,
                                content: 'Error'
                            });
                        }
                    }
                    throw error;
                })) as any;
    }

    private getToken(): string {
        return this.tokenResolverService.accessToken || '';
    }

    private formatErrorMessage(parsedError: any): string {
        return htmlToPlainText(parsedError.message ? parsedError.message : parsedError.Message);
    }

}
